"use client";

import { useDataLayerContext } from "@/datalayer/DataLayerContextProvider";
import { ProductData } from "@/lib/5874/types";
import { isElementInViewport } from "@/utils/dom-utils";
import { useCallback, useEffect, useState } from "react";
import SwiperType from "swiper";

interface UseCarouselVisibilityProps {
	products: ProductData[] | undefined;
	swiper: SwiperType | undefined;
	isLoading?: boolean;
	itemListId?: string;
	itemListName?: string;
	currency?: string;
}

/**
 * A hook to track which products are currently visible in a Swiper carousel.
 * Handles both local state for UI triggers (like ProductsViewedTrigger)
 * and automatic DataLayer (GTM) "view_item_list" events.
 */
export const useCarouselVisibility = ({
	products,
	swiper,
	isLoading = false,
	itemListId,
	itemListName,
	currency,
}: UseCarouselVisibilityProps) => {
	const [visibleProducts, setVisibleProducts] = useState<ProductData[]>([]);
	const { viewItemList } = useDataLayerContext();

	const calculateVisibleProducts = useCallback(() => {
		if (!swiper || !products || products.length === 0) {
			return [];
		}

		let slidesToCheck = swiper.slides.map((slide, index) => ({
			slide,
			index,
		}));

		// If slidesPerView is a number, we can optimize by only checking the active range
		if (typeof swiper.params.slidesPerView === "number") {
			const perView = Number(swiper.params.slidesPerView);
			slidesToCheck = slidesToCheck.slice(
				swiper.activeIndex,
				swiper.activeIndex + perView,
			);
		}

		const visibleSlides = slidesToCheck.filter((item) =>
			isElementInViewport(item.slide),
		);
		const visibleSlideIndexes = visibleSlides.map((item) => item.index);

		if (visibleSlideIndexes.length === 0) {
			return [];
		}

		return products.filter((_, index) => visibleSlideIndexes.includes(index));
	}, [swiper, products]);

	const updateVisibleProducts = useCallback(() => {
		const nextVisible = calculateVisibleProducts();
		setVisibleProducts(nextVisible);
	}, [calculateVisibleProducts]);

	// Initial calculation once loading is finished and swiper is ready
	useEffect(() => {
		if (!isLoading && swiper && products?.length) {
			updateVisibleProducts();
		}
	}, [isLoading, swiper, products, updateVisibleProducts]);

	// Re-evaluate visibility when the carousel enters the viewport
	useEffect(() => {
		if (isLoading || !swiper || !products?.length) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						updateVisibleProducts();
					}
				});
			},
			{
				threshold: [0.8, 0.9, 1.0],
			},
		);

		const target = swiper.el;
		if (target) {
			observer.observe(target);
		}

		return () => {
			if (target) {
				observer.unobserve(target);
			}
			observer.disconnect();
		};
	}, [isLoading, swiper, products, updateVisibleProducts]);

	// Automatic GTM Tracking
	useEffect(() => {
		if (visibleProducts.length > 0 && itemListId && itemListName && currency) {
			viewItemList(visibleProducts, itemListId, currency, itemListName);
		}
	}, [visibleProducts, itemListId, itemListName, currency, viewItemList]);

	return {
		visibleProducts,
		updateVisibleProducts,
	};
};
